import { useEffect, useState, type ReactNode } from 'react'
import { APP_NAME } from '@/constants'
import { UpdateSparklesIcon } from '../icons/icons'
import { useAppUpdateStore } from '../stores/useAppUpdateStore'
import { formatUpdateDuration, secondsUntil } from '../utils/appUpdate'

/** Where the app's shell draws the update banner, above its header (AppShellLayout). */
export const APP_UPDATE_BANNER_HOST = 'data-app-update-banner-host'

/** The current time, again every second while `active`, for a countdown. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (!active) return
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [active])
  return now
}

/**
 * "in about **1m 23s**", or "any moment" once that time has gone by: an
 * estimate that's run out says so instead of counting below zero.
 */
function InAboutOrAnyMoment({ at, now }: { at: number; now: number }) {
  const seconds = secondsUntil(at, now)
  if (seconds === null) return <>any moment</>
  return (
    <>
      in about <strong className="font-bold">{formatUpdateDuration(seconds)}</strong>
    </>
  )
}

function NoticeBar({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      data-app-update=""
      role="region"
      aria-label={label}
      className="flex shrink-0 items-center gap-2 border-b border-warning-subtle surface-warning-subtle px-4 py-2 text-sm text-warning"
    >
      <UpdateSparklesIcon size={16} className="shrink-0" aria-hidden="true" />
      <p>
        <span className="font-semibold uppercase">{label}:</span> {children}
      </p>
    </div>
  )
}

/**
 * The full-width notice above the app header: an update coming (it can't be
 * dismissed, so nobody misses it), or one in progress once the updating
 * modal is closed to show the frozen page. Nothing otherwise, including for
 * an update with nothing to migrate, which never locks anyone out.
 */
export function AppUpdateBanner() {
  const { updating, estimatedEndAt, upcoming, frozenPageShown } = useAppUpdateStore()
  const inProgress = updating && frozenPageShown
  const now = useNow(inProgress || (!updating && upcoming !== null))

  if (inProgress) {
    return (
      <NoticeBar label="Updates in progress">
        {APP_NAME} is updating.
        {estimatedEndAt !== null && (
          <>
            {' '}
            Ready <InAboutOrAnyMoment at={estimatedEndAt} now={now} />.
          </>
        )}{' '}
        The app will reload automatically when the update is complete.
      </NoticeBar>
    )
  }
  if (updating || upcoming === null) return null
  return (
    <NoticeBar label="Updating soon">
      {APP_NAME} will start updating <InAboutOrAnyMoment at={upcoming.startsAt} now={now} />. It
      should only take about{' '}
      <strong className="font-bold">{formatUpdateDuration(upcoming.downtimeSeconds)}</strong>. Save
      your work before updates begin. Unsaved changes will be lost.
    </NoticeBar>
  )
}

/**
 * Covers the app while a deploy holds it (the API's updating 503; see
 * `isUpdatingError`) over the page, grayed out and frozen as it was when the
 * update started. "View frozen page" closes it to that page (still frozen,
 * with the in-progress banner) so anything unsaved can be read or copied.
 * AppUpdateController shows it and reloads onto the new build when the
 * update is over.
 */
export function UpdatingModal() {
  const estimatedEndAt = useAppUpdateStore((state) => state.estimatedEndAt)
  const showFrozenPage = useAppUpdateStore((state) => state.showFrozenPage)
  const now = useNow(estimatedEndAt !== null)

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') showFrozenPage()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [showFrozenPage])

  return (
    <div
      data-app-update=""
      className="fixed inset-0 flex items-center justify-center overflow-auto bg-[color:var(--bg-overlay)] p-4"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-updating-title"
        className="w-full max-w-2xl rounded-2xl border border-default surface-page px-8 py-10 text-center shadow-lg"
      >
        <div className="tone-purple mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[color:var(--tone-100)] text-[color:var(--tone-500)]">
          <UpdateSparklesIcon size={56} aria-hidden="true" />
        </div>
        <h1 id="app-updating-title" className="mt-6 text-3xl font-semibold text-default">
          {APP_NAME} is updating
        </h1>
        {estimatedEndAt !== null && (
          <p className="mt-3 text-2xl font-medium text-default">
            Ready <InAboutOrAnyMoment at={estimatedEndAt} now={now} />
          </p>
        )}
        <p className="mt-4 text-sm text-muted">
          The app will reload automatically when the update is complete.
        </p>
        <p className="mt-3 text-sm text-faint">
          You can view the page frozen exactly as it was when updates started, but any unsaved
          changes have already been lost.
        </p>
        <button
          type="button"
          autoFocus
          onClick={showFrozenPage}
          className="mt-6 inline-flex h-9 items-center rounded-full border border-strong surface-default px-5 text-sm text-subtle hover:bg-lighter hover:text-default"
        >
          View frozen page
        </button>
      </div>
    </div>
  )
}
